import { MeshProperties, Mesh, MeshFields } from "@/types/core/Mesh";
import { Vector3 } from "three";
import * as THREE from "three";
import { Box } from "@react-three/drei";
import { forwardRef, useRef, useMemo } from "react";
import { TransformableItemProperties } from '../../core/Item';
import { Euler } from "three";
import { useAppContext } from "@/context/AppContext";
import { Material } from "@/types/core/Material";

export interface BoxMeshProperties extends TransformableItemProperties {
  width: number;
  height: number;
  depth: number;
  materialId?: string;
}

export interface BoxMesh extends Mesh<BoxMeshProperties> {
  subtype: 'box';
  properties: BoxMeshProperties;
}

// Create a reusable Box component that can be used in both preview and world
const BoxComponent = forwardRef<THREE.Mesh, { properties: BoxMeshProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  const { width, height, depth, transform, materialId } = properties;
  const { activeWorld } = useAppContext();
  
  // In preview mode, use simplified transform
  const position: [number, number, number] = isPreview 
    ? [0, 0, 0] 
    : [transform.position.x, transform.position.y, transform.position.z];
  const rotation: [number, number, number] = isPreview
    ? [0, 0, 0]
    : [transform.rotation.x, transform.rotation.y, transform.rotation.z];
  const scale: [number, number, number] = isPreview
    ? [1, 1, 1]
    : [transform.scale.x, transform.scale.y, transform.scale.z];

  // Get the material from the world if available
  const materialItem = activeWorld && materialId ? activeWorld.items[materialId] : undefined;
  const material = materialItem?.type === 'material' ? (materialItem as unknown as Material) : undefined;

  // Create the material using the render function
  const meshMaterial = useMemo(() => {
    if (material) {
      return material.render(material.properties, isPreview);
    }
    return new THREE.MeshStandardMaterial({ color: "#808080" });
  }, [material, material?.properties, isPreview]);

  return (
    <Box
      ref={ref}
      args={[width, height, depth]}
      position={position}
      rotation={rotation}
      scale={scale}
      material={meshMaterial}
    />
  );
});

export const BoxMeshItem: BoxMesh = {
  id: '',
  name: 'Box',
  type: 'mesh',
  subtype: 'box',
  icon: 'Box',
  properties: {
    width: 1,
    height: 1,
    depth: 1,
    transform: {
      position: new Vector3(),
      rotation: new Euler(),
      scale: new Vector3(1, 1, 1)
    },
    materialId: '' // This will be set when the mesh is created
  },
  fields: [
    ...MeshFields,
    {
      key: 'properties.width',
      type: 'number',
      label: 'Width',
      default: 1,
      panel: 'geometry'
    },
    {
      key: 'properties.height',
      type: 'number',
      label: 'Height',
      default: 1,
      panel: 'geometry'
    },
    {
      key: 'properties.depth',
      type: 'number',
      label: 'Depth',
      default: 1,
      panel: 'geometry'
    }
  ],
  render: (properties: BoxMeshProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <BoxComponent properties={properties} isPreview={isPreview} ref={ref as React.RefObject<THREE.Mesh>} />;
  }
};
